import React, { useEffect, useState, useCallback, useMemo } from 'react';

interface HeroImage {
  src: string;
  placeholder: string;
  alt: string;
}

const HeroSection: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [isTransitioning, setIsTransitioning] = useState(false);

  const heroImages: HeroImage[] = useMemo(() => [
    {
      src: "/images/hero/optimized/Baltic_blooms_ESA21514635.jpeg",
      placeholder: "/images/hero/placeholders/Baltic_blooms_ESA21514635.jpeg",
      alt: "Baltic Blooms"
    },
    {
      src: "/images/hero/optimized/Guineabissau_oli_2018137_lrg.jpg",
      placeholder: "/images/hero/placeholders/Guineabissau_oli_2018137_lrg.jpg",
      alt: "Guinea Bissau"
    },
    {
      src: "/images/hero/optimized/auskingsound_oli2_20250611_lrg.jpg",
      placeholder: "/images/hero/placeholders/auskingsound_oli2_20250611_lrg.jpg",
      alt: "King Sound Australia"
    },
    {
      src: "/images/hero/optimized/drytortugas_oli2_20250320_lrg.jpg",
      placeholder: "/images/hero/placeholders/drytortugas_oli2_20250320_lrg.jpg",
      alt: "Dry Tortugas"
    },
    {
      src: "/images/hero/optimized/norwegianbloom_pce_20250712_lrg.jpg",
      placeholder: "/images/hero/placeholders/norwegianbloom_pce_20250712_lrg.jpg",
      alt: "Norwegian Bloom"
    }
  ], []);

  // Preload images
  const preloadImage = useCallback((index: number) => {
    const img = new Image();
    img.onload = () => {
      setLoadedImages(prev => new Set(prev).add(index));
    };
    img.src = heroImages[index].src;
  }, [heroImages]);

  // Preload current and next image
  useEffect(() => {
    preloadImage(currentImageIndex);
    const nextIndex = (currentImageIndex + 1) % heroImages.length;
    preloadImage(nextIndex);
  }, [currentImageIndex, preloadImage, heroImages.length]);

  // Carousel rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) =>
          (prevIndex + 1) % heroImages.length
        );
        setIsTransitioning(false);
      }, 300);
    }, 8000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <header
      id="home"
      className="hero-section d-flex align-items-center justify-content-center text-white"
      style={{ position: 'relative', overflow: 'hidden', height: '100vh' }}
    >
      <div id="heroCarousel" className="hero-carousel">
        <div className="carousel-inner">
          {heroImages.map((image, index) => {
            const isActive = index === currentImageIndex;
            const isLoaded = loadedImages.has(index);

            return (
              <div
                key={index}
                className={`carousel-item ${isActive ? 'active' : ''}`}
                style={{
                  opacity: isActive && !isTransitioning ? 1 : 0,
                  transition: 'opacity 0.8s ease-in-out',
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0
                }}
              >
                {/* Placeholder with blur */}
                <img
                  src={image.placeholder}
                  className="d-block w-100 hero-image"
                  alt={image.alt}
                  style={{
                    filter: isLoaded ? 'none' : 'blur(20px)',
                    transform: 'scale(1.1)',
                    transition: 'filter 0.5s ease-out',
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />

                {/* Full quality image */}
                {(isActive || loadedImages.has(index)) && (
                  <img
                    src={image.src}
                    className="d-block w-100 hero-image"
                    alt={image.alt}
                    loading={index === 0 ? "eager" : "lazy"}
                    style={{
                      opacity: isLoaded ? 1 : 0,
                      transition: 'opacity 0.5s ease-in',
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onLoad={() => setLoadedImages(prev => new Set(prev).add(index))}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="hero-overlay" style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)',
        zIndex: 1
      }}></div>

      <div className="hero-content text-center" style={{
        position: 'relative',
        zIndex: 2,
        maxWidth: '1100px',
        padding: '0 20px'
      }}>
        <h1 style={{
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 600,
          lineHeight: 1.2,
          marginBottom: '1.5rem',
          color: 'rgba(255, 255, 255, 0.95)',
          textShadow: '2px 2px 8px rgba(0, 0, 0, 0.4)',
          letterSpacing: '-0.02em'
        }}>
          Ocean Intelligence for Conservation<br className="d-none d-md-block" /> and Nature-Positive Finance
        </h1>
        <p style={{
          fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
          fontWeight: 300,
          lineHeight: 1.6,
          marginBottom: '2rem',
          color: 'rgba(255, 255, 255, 0.85)',
          textShadow: '1px 1px 4px rgba(0, 0, 0, 0.3)',
          maxWidth: '900px',
          margin: '0 auto 2rem'
        }}>
          Ocean BioMetrics Ltd. transforms complex biological data into actionable insights{' '}
          <br className="d-none d-md-block" />
          that power ecosystem protection and a regenerative blue economy
        </p>
      </div>
    </header>
  );
};

export default HeroSection;